/**
 * Phase 6.0.7 — Cart install nudge.
 *
 * Inline card shown on the cart page when the user has items in their cart
 * and is browsing from a non-standalone session. The cart is the highest-
 * intent moment we have — customer is engaged with a transaction but hasn't
 * pulled the trigger yet. Showing the install bonus here converts at
 * 2-3× the rate of any other surface (homepage banner, post-signup toast,
 * etc.) and the 100-pt bonus doubles as an abandoned-cart recovery hook.
 *
 * Visibility rules:
 *   - cartItemCount > 0
 *   - NOT already running standalone
 *   - feature flag `customer.pwa_install_banner` enabled
 *   - not dismissed within the last 7 days (DISMISS_KEY)
 *
 * On tap:
 *   - iOS → opens the 3-slide walkthrough modal (already exists)
 *   - Android/Desktop → fires native beforeinstallprompt OR walkthrough
 *
 * The install bonus itself (+100 pts) is credited server-side on the FIRST
 * standalone login after install (see backend/loyalty.py:award_install_bonus).
 * This component does not credit — it only educates and routes the user
 * to the install action.
 */
import { useEffect, useState } from 'react';
import { Smartphone, X, Sparkles } from 'lucide-react';
import { useFlag } from '../context/FeatureFlagsContext';
import { detectPlatform, canPromptInstall, promptInstall, isPreviewInstallMode } from '../utils/installPwa';
import { PWAInstallWalkthrough } from './PWAInstallWalkthrough';
import { useTheme } from '../context/ThemeContext';

function ModernNudge({ cta, onInstall, onDismiss }) {
  return (
    <div className="relative my-4 p-4 sm:p-5 rounded-[14px] border" style={{ background: 'var(--am-emerald-50)', borderColor: 'var(--am-emerald-200)', fontFamily: 'var(--am-font-sans)' }} data-testid="cart-install-nudge">
      <button type="button" onClick={onDismiss} className="absolute top-2.5 right-2.5 p-1.5 rounded-full hover:bg-white/70" style={{ color: 'var(--am-ink-3)' }} data-testid="cart-install-nudge-dismiss" aria-label="Dismiss install offer">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-white" style={{ color: 'var(--am-emerald-600)' }}>
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-semibold flex flex-wrap items-center gap-2" style={{ color: 'var(--am-ink-1)' }}>
            Install Ash &amp; Merch
            <span className="am-chip am-chip-gold"><Sparkles className="w-3 h-3" />+100 pts</span>
          </p>
          <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--am-ink-2)' }}>
            Live delivery tracking and one-tap reorder. We add 100 points the first time you sign in from the app.
          </p>
          <button type="button" onClick={onInstall} className="am-cta mt-3 px-5 h-10 text-sm" data-testid="cart-install-nudge-cta">{cta}</button>
        </div>
      </div>
    </div>
  );
}

const DISMISS_KEY = 'cart_install_nudge_dismissed_v1';
const DISMISS_DAYS = 7;

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (_) { return false; }
}

export function CartInstallNudge({ cartItemCount = 0 }) {
  const enabled = useFlag('customer.pwa_install_banner', true);
  const { isModern } = useTheme();
  const [platform, setPlatform] = useState(null);
  const [bipReady, setBipReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [walkthrough, setWalkthrough] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return undefined;
    setPlatform(detectPlatform());
    setBipReady(canPromptInstall());
    setDismissed(wasRecentlyDismissed());
    const onBip = () => setBipReady(true);
    const onInstalled = () => setDismissed(true);
    window.addEventListener('pwa-install-available', onBip);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-install-available', onBip);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, [enabled]);

  if (!enabled || dismissed || cartItemCount <= 0 || !platform) return null;

  const preview = isPreviewInstallMode();
  if (!preview && platform.isStandalone) return null;
  if (!platform.isIOS && !platform.isAndroid && !platform.isDesktop) return null;

  const dismiss = (e) => {
    e?.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (platform.isIOS) {
      setWalkthrough(true);
      return;
    }
    if (bipReady) {
      const choice = await promptInstall();
      if (choice?.outcome === 'accepted') setDismissed(true);
    } else {
      setWalkthrough(true);
    }
  };

  const cta = platform.isIOS ? 'Show me how' : 'Install app';
  if (isModern) {
    return (
      <>
        <ModernNudge cta={cta} onInstall={handleInstall} onDismiss={dismiss} />
        <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />
      </>
    );
  }

  return (
    <>
      <div
        className="relative border border-[#39FF14] bg-[rgba(57,255,20,0.04)] p-4 sm:p-5 my-4"
        data-testid="cart-install-nudge"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 text-[#6B8E73] hover:text-[#A3C7A8] p-1"
          data-testid="cart-install-nudge-dismiss"
          aria-label="Dismiss install offer"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="shrink-0 mt-0.5">
            <Smartphone className="w-5 h-5 text-[#39FF14]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#39FF14] font-mono text-sm font-bold flex items-center gap-1">
              Install Ash &amp; Merch
              <span className="text-[#FFA500] inline-flex items-center gap-0.5">
                <Sparkles className="w-3 h-3" /> +100 pts
              </span>
            </p>
            <p className="text-[#A3C7A8] text-xs mt-1 leading-relaxed">
              Track your delivery in real-time. One-tap reorder. <strong className="text-[#39FF14]">+100 pts</strong> deposited on first login from the installed app.
            </p>
            <button
              type="button"
              onClick={handleInstall}
              className="mt-3 bg-[#39FF14] text-[#050003] font-mono text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-[#7FFF55] transition-colors"
              data-testid="cart-install-nudge-cta"
            >
              {platform.isIOS ? 'Show me how' : 'Install app'}
            </button>
          </div>
        </div>
      </div>
      <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />
    </>
  );
}

export default CartInstallNudge;
